import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Input, Space, Row, Col, Statistic,
    Typography, Tabs, Avatar, Button, DatePicker, Dropdown, message
} from 'antd';
import {
    SearchOutlined, PercentageOutlined,
    TeamOutlined, CrownOutlined, ReloadOutlined,
    EyeOutlined, DollarOutlined, CalendarOutlined,
    DownloadOutlined, FileExcelOutlined, UserOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';
import XLSX from 'xlsx-js-style';

dayjs.locale('fr');

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const RevenuesMonthlyRetentionsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [retentions, setRetentions] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);

    const [stats, setStats] = useState({
        totalBeneficiaries: 0,
        totalRetention: 0,
        affiliateCount: 0,
        affiliateAmount: 0,
        ambassadorCount: 0,
        ambassadorAmount: 0
    });

    const fetchRetentions = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (activeTab !== 'all') {
                params.type = activeTab;
            }
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].startOf('month').toISOString();
                params.endDate = dateRange[1].endOf('month').toISOString();
            }

            const response = await affiliatesService.getMonthlyRetentionsList(params);
            if (response.success) {
                setRetentions(response.data || []);
                if (response.stats) {
                    setStats(response.stats);
                }
            }
        } catch (error) {
            console.error('Erreur chargement rétentions:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, dateRange]);

    useEffect(() => {
        fetchRetentions();
    }, [fetchRetentions]);

    const filteredRetentions = retentions.filter(b => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        const name = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        const phone = (b.phone || '').toLowerCase();
        return name.includes(search) || phone.includes(search);
    });

    const handleViewDetails = (record) => {
        navigate(`/dashboard/revenues-monthly-retentions/${record.type}/${record.beneficiaryId}`);
    };

    const getTypeTag = (type) => {
        if (type === 'affiliate') {
            return <Tag color="blue" icon={<TeamOutlined />}>Affilié</Tag>;
        }
        return <Tag color="purple" icon={<CrownOutlined />}>Ambassadeur</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        const dataToExport = filteredRetentions.filter(b => activeTab === 'all' || b.type === activeTab);
        return dataToExport.map(b => ({
            'Nom': `${b.firstName || ''} ${b.lastName || ''}`.trim(),
            'Téléphone': b.phone || '',
            'Type': b.type === 'affiliate' ? 'Affilié' : 'Ambassadeur',
            'Chauffeurs': b.driversCount || 0,
            'Total Recharges (F)': b.totalRecharges || 0,
            'Rétention (F)': b.totalRetention || 0,
            'Périodes actives': b.periodsCount || 0
        }));
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            let titleLine = 'RÉTENTIONS MENSUELLES - TOUS LES BÉNÉFICIAIRES';
            if (activeTab === 'affiliate') titleLine = 'RÉTENTIONS MENSUELLES - AFFILIÉS';
            else if (activeTab === 'ambassador') titleLine = 'RÉTENTIONS MENSUELLES - AMBASSADEURS';

            let periodLine = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodLine = `PÉRIODE: ${dateRange[0].format('MM/YYYY')} - ${dateRange[1].format('MM/YYYY')}`;
            } else {
                periodLine = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headers = Object.keys(data[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...data.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `retentions_mensuelles_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel
    const exportToExcel = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "722ED1" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            let titleText = 'RÉTENTIONS MENSUELLES - TOUS LES BÉNÉFICIAIRES';
            if (activeTab === 'affiliate') titleText = 'RÉTENTIONS MENSUELLES - AFFILIÉS';
            else if (activeTab === 'ambassador') titleText = 'RÉTENTIONS MENSUELLES - AMBASSADEURS';

            let periodText = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodText = `PÉRIODE: ${dateRange[0].format('MM/YYYY')} - ${dateRange[1].format('MM/YYYY')}`;
            } else {
                periodText = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(data[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...data.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Rétentions Mensuelles');

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const filename = `retentions_mensuelles_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            width: 280,
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={45}
                        icon={record.type === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                        style={{
                            backgroundColor: record.type === 'affiliate' ? '#1890ff' : '#722ed1',
                            flexShrink: 0
                        }}
                    />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ color: '#888', fontSize: 12 }}>{record.phone}</div>
                        {getTypeTag(record.type)}
                    </div>
                </div>
            )
        },
        {
            title: 'Chauffeurs',
            dataIndex: 'driversCount',
            key: 'driversCount',
            width: 110,
            sorter: (a, b) => (a.driversCount || 0) - (b.driversCount || 0),
            render: (count) => (
                <div style={{ textAlign: 'center' }}>
                    <UserOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                    <span style={{ fontWeight: 600 }}>{count || 0}</span>
                </div>
            )
        },
        {
            title: 'Total Recharges',
            dataIndex: 'totalRecharges',
            key: 'totalRecharges',
            width: 150,
            sorter: (a, b) => (a.totalRecharges || 0) - (b.totalRecharges || 0),
            render: (amount) => (
                <div style={{ fontWeight: 500, color: '#1890ff' }}>
                    {(amount || 0).toLocaleString()} F
                </div>
            )
        },
        {
            title: 'Rétention',
            dataIndex: 'totalRetention',
            key: 'totalRetention',
            width: 140,
            sorter: (a, b) => (a.totalRetention || 0) - (b.totalRetention || 0),
            render: (amount, record) => (
                <div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#3f8600' }}>
                        {(amount || 0).toLocaleString()} F
                    </div>
                    <div style={{ fontSize: 11, color: '#888' }}>
                        {record.type === 'affiliate' ? '1%' : '1% + 0.2%'}
                    </div>
                </div>
            )
        },
        {
            title: 'Périodes',
            dataIndex: 'periodsCount',
            key: 'periodsCount',
            width: 110,
            sorter: (a, b) => (a.periodsCount || 0) - (b.periodsCount || 0),
            render: (count) => (
                <div style={{ textAlign: 'center' }}>
                    <CalendarOutlined style={{ marginRight: 6, color: '#722ed1' }} />
                    <span style={{ fontWeight: 600 }}>{count || 0}</span>
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record)}
                >
                    Détail
                </Button>
            )
        }
    ];

    return (
        <div className="monthly-retentions-page">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <PercentageOutlined style={{ marginRight: 12, color: '#722ed1' }} />
                        Rétentions Mensuelles
                    </Title>
                    <Text type="secondary">
                        Commissions sur les recharges mensuelles des chauffeurs (1% affilié / 0.2% ambassadeur)
                    </Text>
                </div>
                <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchRetentions}
                        loading={loading}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Statistiques */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><PercentageOutlined style={{ marginRight: 8, color: '#722ed1' }} />Total rétentions</span>}
                            value={(stats.totalRetention || 0).toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#722ed1' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Bénéficiaires: <strong>{stats.totalBeneficiaries || 0}</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />Affiliés</span>}
                            value={stats.affiliateCount}
                            valueStyle={{ color: '#1890ff' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Montant: <strong>{(stats.affiliateAmount || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><CrownOutlined style={{ marginRight: 8, color: '#722ed1' }} />Ambassadeurs</span>}
                            value={stats.ambassadorCount}
                            valueStyle={{ color: '#722ed1' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Montant: <strong>{(stats.ambassadorAmount || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><DollarOutlined style={{ marginRight: 8, color: '#3f8600' }} />Montant total</span>}
                            value={(stats.totalRetention || 0).toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Recherche et filtres */}
            <Card style={{ marginBottom: 16 }}>
                <Space wrap size="middle">
                    <Input
                        placeholder="Rechercher par nom ou téléphone..."
                        prefix={<SearchOutlined />}
                        style={{ width: 280 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                    <RangePicker
                        picker="month"
                        placeholder={['Mois début', 'Mois fin']}
                        format="MM/YYYY"
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates)}
                        allowClear
                        style={{ width: 280 }}
                        locale={locale}
                    />
                    {dateRange && (
                        <Tag color="purple" closable onClose={() => setDateRange(null)}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {dateRange[0]?.format('MM/YYYY')} - {dateRange[1]?.format('MM/YYYY')}
                        </Tag>
                    )}
                </Space>
            </Card>

            {/* Onglets et tableau */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={<span><PercentageOutlined /> Tous ({retentions.length})</span>}
                        key="all"
                    />
                    <TabPane
                        tab={<span><TeamOutlined style={{ color: '#1890ff' }} /> Affiliés ({retentions.filter(b => b.type === 'affiliate').length})</span>}
                        key="affiliate"
                    />
                    <TabPane
                        tab={<span><CrownOutlined style={{ color: '#722ed1' }} /> Ambassadeurs ({retentions.filter(b => b.type === 'ambassador').length})</span>}
                        key="ambassador"
                    />
                </Tabs>

                <Table
                    columns={columns}
                    dataSource={filteredRetentions}
                    rowKey={(record) => `${record.type}-${record.beneficiaryId}`}
                    loading={loading}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `${total} bénéficiaire(s)`,
                        defaultPageSize: 20
                    }}
                    scroll={{ x: 900 }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default RevenuesMonthlyRetentionsPage;
